import { useEffect } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "./lib/api";
import { initTheme } from "./lib/theme";
import Layout from "./components/Layout";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { PromptProvider } from "./components/PromptDialog";
import { ToastProvider } from "./components/Toast";
import LoadingBar from "./components/LoadingBar";
import PageSpinner from "./components/PageSpinner";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import InvitePage from "./pages/Invite";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyGate from "./pages/VerifyGate";
import WorkspacePage from "./pages/Workspace";
import Runner from "./pages/Runner";
import OAuthCallback from "./pages/OAuthCallback";

export default function App() {
  useEffect(() => initTheme(), []);
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.me() });
  if (me.isLoading) return (<><LoadingBar /><PageSpinner /></>);

  const user = me.data?.user ?? null;

  return (
    <ToastProvider>
    <ConfirmProvider>
    <PromptProvider>
      <Routes>
        <Route path="/invite/:token" element={<InvitePage user={user} />} />
        <Route path="/reset/:token" element={<ResetPassword />} />
        <Route path="/verify/:token" element={<VerifyEmail />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        {!user ? (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : user.emailVerified === false ? (
          // Hard gate: a signed-in but unverified account can't reach the app.
          <Route path="*" element={<VerifyGate user={user} />} />
        ) : (
          <Route element={<Layout user={user} />}>
            <Route path="/" element={<WorkspacePage />} />
            <Route path="/c/:collectionId" element={<WorkspacePage />} />
            <Route path="/r/:requestId" element={<WorkspacePage />} />
            <Route path="/w/:webhookId" element={<WorkspacePage />} />
            <Route path="/f/:flowId" element={<WorkspacePage />} />
            <Route path="/runner" element={<Runner />} />
            <Route path="/runner/:runId" element={<Runner />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        )}
      </Routes>
    </PromptProvider>
    </ConfirmProvider>
    </ToastProvider>
  );
}
